import React, { useEffect, useState } from "react";

const MarketSection = () => {
  const [marketData, setMarketData] = useState([]);
  const [loadingMarkets, setLoadingMarkets] = useState(true);
  const [marketsError, setMarketsError] = useState("");
  const [lastMarketUpdate, setLastMarketUpdate] = useState("");

  useEffect(() => {
    let intervalId;
    let isMounted = true;

    const fetchMarkets = async () => {
      try {
        if (!isMounted) return;

        setMarketsError("");

        const response = await fetch(
          "https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=bitcoin,ethereum,tether,binancecoin,solana,xrp,dogecoin,tron,cardano,toncoin&order=market_cap_desc&per_page=10&page=1&sparkline=false&price_change_percentage=24h"
        );

        if (!response.ok) {
          throw new Error("Failed to fetch market data");
        }

        const data = await response.json();

        if (!isMounted) return;

        setMarketData((prevData) => {
          if (!Array.isArray(prevData) || prevData.length === 0) {
            return data;
          }

          return data.map((coin) => {
            const previousCoin = prevData.find((item) => item.id === coin.id);

            return {
              ...coin,
              previous_price: previousCoin
                ? previousCoin.current_price
                : coin.current_price,
            };
          });
        });

        setLastMarketUpdate(new Date().toLocaleTimeString());
      } catch (error) {
        console.error("Market fetch error:", error);

        if (isMounted) {
          setMarketsError("Unable to refresh markets");
        }
      } finally {
        if (isMounted) {
          setLoadingMarkets(false);
        }
      }
    };

    fetchMarkets();
    intervalId = setInterval(fetchMarkets, 15000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const formatPrice = (price) => {
    const numericPrice = Number(price || 0);

    if (numericPrice >= 1000) {
      return `$${numericPrice.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      })}`;
    }

    if (numericPrice >= 1) {
      return `$${numericPrice.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    if (numericPrice >= 0.01) {
      return `$${numericPrice.toFixed(4)}`;
    }

    return `$${numericPrice.toFixed(6)}`;
  };

  const formatChange = (change) => {
    if (change === null || change === undefined || Number.isNaN(change)) {
      return "0.00%";
    }

    return `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`;
  };

  const getMarketCardClass = (coin) => {
    if (
      coin.previous_price !== undefined &&
      Number(coin.current_price) > Number(coin.previous_price)
    ) {
      return "market-card market-card-updated-up";
    }

    if (
      coin.previous_price !== undefined &&
      Number(coin.current_price) < Number(coin.previous_price)
    ) {
      return "market-card market-card-updated-down";
    }

    return "market-card";
  };

  return (
    <section className="market-section">
      <div className="section-head">
        <h3>Markets</h3>

        <div className="market-meta">
          {lastMarketUpdate ? (
            <span className="market-last-update">
              Updated {lastMarketUpdate}
            </span>
          ) : null}
        </div>
      </div>

      <div className="market-list">
        {loadingMarkets ? (
          <div className="market-loading">Loading...</div>
        ) : marketsError ? (
          <div className="market-loading">{marketsError}</div>
        ) : (
          marketData.map((coin) => (
            <div className={getMarketCardClass(coin)} key={coin.id}>
              <div className="market-left">
                <div className="market-icon-image-wrap">
                  <img
                    src={coin.image}
                    alt={coin.name}
                    className="market-icon-image"
                  />
                </div>

                <div className="market-text">
                  <h4>{coin.symbol.toUpperCase()}</h4>
                  <p>{coin.name}</p>
                </div>
              </div>

              <div className="market-right">
                <h5>{formatPrice(coin.current_price)}</h5>

                <span
                  className={
                    coin.price_change_percentage_24h < 0
                      ? "market-change down"
                      : "market-change up"
                  }
                >
                  {formatChange(coin.price_change_percentage_24h)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default MarketSection;